import React, { useState, useEffect, useRef } from "react";
import ReactDOM from 'react-dom';
import { fetchAllCampaigns } from "../../services/CampaignService";
import { fetchAllDonors, fetchAllShipments, fetchAllDonations } from "../../services/DonationService";
import type { DonationAssignedStatus } from "../../types/donation.types";
import dayjs from "dayjs";
import { Button } from '../../components/Button';

interface DonationFiltersModalProps {
    open: boolean;
    onClose: () => void;
    onApply?: (donations: any[], filters: any) => void;
}

const DONATION_STATUSES: { label: string; value: DonationAssignedStatus }[] = [
    { label: "Unassigned", value: "UNASSIGNED" },
    { label: "Assigned", value: "ASSIGNED" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Missing", value: "MISSING" },
];

export default function DonationFiltersModal({ open, onClose, onApply }: DonationFiltersModalProps) {
    const [campaigns, setCampaigns] = useState<{ label: string; value: string | number }[]>([]);
    const [campaignSearch, setCampaignSearch] = useState("");
    const [selectedCampaigns, setSelectedCampaigns] = useState<{ label: string; value: string | number }[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedDonors, setSelectedDonors] = useState<{ label: string; value: string | number }[]>([]);
    const [selectedShipments, setSelectedShipments] = useState<{ label: string; value: string | number }[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<{ label: string; value: string | number }[]>([]);
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(1, "day").format("YYYY-MM-DD"));
    const [dateTo, setDateTo] = useState(() => dayjs().add(1, "day").format("YYYY-MM-DD"));
    const [donors, setDonors] = useState<{ label: string; value: string | number }[]>([]);
    const [donorSearch, setDonorSearch] = useState("");
    const [showDonorDropdown, setShowDonorDropdown] = useState(false);
    const donorInputRef = useRef<HTMLInputElement>(null);
    const [shipments, setShipments] = useState<{ label: string; value: string | number }[]>([]);
    const [shipmentSearch, setShipmentSearch] = useState("");
    const [showShipmentDropdown, setShowShipmentDropdown] = useState(false);
    const shipmentInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        const loadCampaigns = async () => {
            try {
                const response = await fetchAllCampaigns();
                setCampaigns(
                    (response?.data || []).map((c: any) => ({
                        label: c.name,
                        value: c.id,
                    }))
                );
            } catch (err) {
                setCampaigns([]);
            }
        };
        loadCampaigns();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const loadDonors = async () => {
            try {
                const response = await fetchAllDonors();
                setDonors(
                    (response?.data || []).map((d: any) => ({
                        label: d.user?.name || "Unknown",
                        value: d.id,
                    }))
                );
            } catch (err) {
                setDonors([]);
            }
        };
        loadDonors();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const loadShipments = async () => {
            try {
                const response = await fetchAllShipments();
                setShipments(
                    (response?.data || []).map((s: any) => ({
                        label: s.name || "Unknown",
                        value: s.id,
                    }))
                );
            } catch (err) {
                setShipments([]);
            }
        };
        loadShipments();
    }, [open]);

    const filteredCampaigns = campaigns.filter(c =>
        c.label.toLowerCase().includes(campaignSearch.toLowerCase()) &&
        !selectedCampaigns.some(sel => sel.value === c.value)
    );

    const filteredDonors = donors.filter(d =>
        (d.label ?? "").toLowerCase().includes((donorSearch ?? "").toLowerCase()) &&
        !selectedDonors.some(sel => sel.value === d.value)
    );

    const filteredShipments = shipments.filter(s =>
        (s.label ?? "").toLowerCase().includes((shipmentSearch ?? "").toLowerCase()) &&
        !selectedShipments.some(sel => sel.value === s.value)
    );

    const filteredStatuses = DONATION_STATUSES.filter(
        s => !selectedStatuses.some(sel => sel.value === s.value)
    );

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        }
        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDropdown]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const statusInput = document.getElementById("status-input");
            if (
                statusInput &&
                !statusInput.contains(event.target as Node)
            ) {
                setShowStatusDropdown(false);
            }
        }
        if (showStatusDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showStatusDropdown]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                donorInputRef.current &&
                !donorInputRef.current.contains(event.target as Node)
            ) {
                setShowDonorDropdown(false);
            }
        }
        if (showDonorDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDonorDropdown]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                shipmentInputRef.current &&
                !shipmentInputRef.current.contains(event.target as Node)
            ) {
                setShowShipmentDropdown(false);
            }
        }
        if (showShipmentDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showShipmentDropdown]);

    const handleApply = async () => {
        const filters: any = {
            campaignIds: selectedCampaigns.map(c => c.value),
            donorIds: selectedDonors.map(d => d.value),
            shipmentIds: selectedShipments.map(s => s.value),
            statuses: selectedStatuses.map(s => s.value),
            showDeactivated,
            fromDate: dateFrom,
            toDate: dateTo,
        };

        Object.keys(filters).forEach(key => {
            if (
                filters[key] === undefined ||
                (Array.isArray(filters[key]) && filters[key].length === 0) ||
                filters[key] === ""
            ) {
                delete filters[key];
            }
        });

        let donations: any[] = [];
        try {
            const response = await fetchAllDonations(filters);
            donations = response?.data || [];
        } catch (err) {
            donations = [];
        }

        if (onApply) {
            onApply(donations, filters);
        }
        onClose();
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-white/50 dark:border-white/10 shadow-xl w-full max-w-lg p-8 relative">
                <Button
                    variant="tertiary"
                    size="sm"
                    className="absolute top-4 right-4 text-2xl"
                    onClick={onClose}
                    aria-label="Close"
                >
                    &times;
                </Button>
                <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-gray-100">Filters</h2>
                <div className="flex flex-col gap-5">
                    {/* Campaign */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Campaign</label>
                        <div className="relative">
                            <input
                                ref={inputRef}
                                className="w-full border rounded px-3 py-2 mb-2 pr-8 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                placeholder="Select campaign"
                                value={campaignSearch}
                                onFocus={() => setShowDropdown(true)}
                                onChange={e => {
                                    setCampaignSearch(e.target.value);
                                    setShowDropdown(true);
                                }}
                            />
                            {/* Chevron icon */}
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            {showDropdown && filteredCampaigns.length > 0 && (
                                <div className="absolute left-0 right-0 bg-white dark:bg-slate-800 border dark:border-white/10 rounded shadow max-h-40 overflow-y-auto z-20">
                                    {filteredCampaigns.map(c => (
                                        <div
                                            key={c.value}
                                            className="px-3 py-2 cursor-pointer hover:bg-primary-50 dark:hover:bg-slate-700"
                                            onMouseDown={() => {
                                                setSelectedCampaigns([...selectedCampaigns, c]);
                                                setCampaignSearch("");
                                                setShowDropdown(false);
                                            }}
                                        >
                                            {c.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {selectedCampaigns.map((c, idx) => (
                                <span
                                    key={c.value}
                                    className="bg-primary-50 text-primary-100 px-3 py-1 rounded-full flex items-center text-sm font-medium"
                                >
                                    {c.label}
                                    <button
                                        className="ml-2 text-primary-100 hover:text-primary-70"
                                        onClick={() =>
                                            setSelectedCampaigns(selectedCampaigns.filter((_, i) => i !== idx))
                                        }
                                        tabIndex={-1}
                                    >
                                        &#10005;
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* Donor */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Donor</label>
                        <div className="relative">
                            <input
                                ref={donorInputRef}
                                className="w-full border rounded px-3 py-2 mb-2 pr-8 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                placeholder="Select donor"
                                value={donorSearch}
                                onFocus={() => setShowDonorDropdown(true)}
                                onChange={e => {
                                    setDonorSearch(e.target.value);
                                    setShowDonorDropdown(true);
                                }}
                            />
                            {/* Chevron icon */}
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            {showDonorDropdown && filteredDonors.length > 0 && (
                                <div className="absolute left-0 right-0 bg-white dark:bg-slate-800 border dark:border-white/10 rounded shadow max-h-40 overflow-y-auto z-20">
                                    {filteredDonors.map(d => (
                                        <div
                                            key={d.value}
                                            className="px-3 py-2 cursor-pointer hover:bg-primary-50 dark:hover:bg-slate-700"
                                            onMouseDown={() => {
                                                setSelectedDonors([...selectedDonors, d]);
                                                setDonorSearch("");
                                                setShowDonorDropdown(false);
                                            }}
                                        >
                                            {d.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {selectedDonors.map((d, idx) => (
                                <span key={d.value} className="bg-primary-50 text-primary-100 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                    {d.label}
                                    <button
                                        className="ml-2 text-primary-100 hover:text-primary-70"
                                        onClick={() => setSelectedDonors(selectedDonors.filter((_, i) => i !== idx))}
                                        tabIndex={-1}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* Shipment */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Shipment</label>
                        <div className="relative">
                            <input
                                ref={shipmentInputRef}
                                className="w-full border rounded px-3 py-2 mb-2 pr-8 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                placeholder="Select shipment"
                                value={shipmentSearch}
                                onFocus={() => setShowShipmentDropdown(true)}
                                onChange={e => {
                                    setShipmentSearch(e.target.value);
                                    setShowShipmentDropdown(true);
                                }}
                            />
                            {/* Chevron icon */}
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            {showShipmentDropdown && filteredShipments.length > 0 && (
                                <div className="absolute left-0 right-0 bg-white dark:bg-slate-800 border dark:border-white/10 rounded shadow max-h-40 overflow-y-auto z-20">
                                    {filteredShipments.map(s => (
                                        <div
                                            key={s.value}
                                            className="px-3 py-2 cursor-pointer hover:bg-primary-50 dark:hover:bg-slate-700"
                                            onMouseDown={() => {
                                                setSelectedShipments([...selectedShipments, s]);
                                                setShipmentSearch("");
                                                setShowShipmentDropdown(false);
                                            }}
                                        >
                                            {s.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {selectedShipments.map((s, idx) => (
                                <span key={s.value} className="bg-primary-50 text-primary-100 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                    {s.label}
                                    <button
                                        className="ml-2 text-primary-100 hover:text-primary-70"
                                        onClick={() => setSelectedShipments(selectedShipments.filter((_, i) => i !== idx))}
                                        tabIndex={-1}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* Status */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Status</label>
                        <div className="relative">
                            <input
                                id="status-input"
                                className="w-full border rounded px-3 py-2 mb-2 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                placeholder="Select status"
                                value={""}
                                onFocus={() => setShowStatusDropdown(true)}
                                readOnly
                            />
                            {/* Chevron icon */}
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            {showStatusDropdown && filteredStatuses.length > 0 && (
                                <div className="absolute left-0 right-0 bg-white dark:bg-slate-800 border dark:border-white/10 rounded shadow max-h-40 overflow-y-auto z-20">
                                    {filteredStatuses.map(s => (
                                        <div
                                            key={s.value}
                                            className="px-3 py-2 cursor-pointer hover:bg-primary-50 dark:hover:bg-slate-700"
                                            onMouseDown={() => {
                                                setSelectedStatuses([...selectedStatuses, s]);
                                                setShowStatusDropdown(false);
                                            }}
                                        >
                                            {s.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {selectedStatuses.map((s, idx) => (
                                <span key={s.value} className="bg-primary-50 text-primary-100 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                    {s.label}
                                    <button
                                        className="ml-2 text-primary-100 hover:text-primary-70"
                                        onClick={() => setSelectedStatuses(selectedStatuses.filter((_, i) => i !== idx))}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* Show deactivated */}
                    <div className="flex items-center gap-3">
                        <label className="text-gray-700 dark:text-gray-300">Show deactivated</label>
                        <label className="inline-flex relative items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={showDeactivated}
                                onChange={() => setShowDeactivated((v) => !v)}
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-primary-100 transition-all"></div>
                            <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${showDeactivated ? "translate-x-5" : ""}`}></div>
                        </label>
                    </div>
                    {/* Date range */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-gray-700 dark:text-gray-300 mb-1">Date range</label>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
                                    <input
                                        type="date"
                                        className="w-full border rounded px-3 py-2 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                        value={dateFrom}
                                        onChange={e => setDateFrom(e.target.value)}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
                                    <input
                                        type="date"
                                        className="w-full border rounded px-3 py-2 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                                        value={dateTo}
                                        onChange={e => setDateTo(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Footer */}
                <div className="flex items-center justify-between mt-8">
                    <Button
                        variant="tertiary"
                        size="md"
                        onClick={() => {
                            setSelectedCampaigns([]);
                            setSelectedDonors([]);
                            setSelectedShipments([]);
                            setSelectedStatuses([]);
                            setShowDeactivated(false);
                            setDateFrom("");
                            setDateTo("");
                        }}
                    >
                        Clear all
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    ,
    document.body
  );
}
